import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createPractitionerConversation,
  fetchChatUnreadCount,
  fetchClientConversations,
  fetchConversationMessages,
  fetchPractitionerConversationById,
  fetchPractitionerConversations,
  initChatAttachmentUpload,
  markConversationRead,
  sendChatMessage,
} from '@/api/chat/chatApi';
import type {
  ChatAttachmentInit,
  ChatAttachmentInitPayload,
  ChatConversation,
  ChatMessage,
  ChatScope,
} from '@/api/chat/chatApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import {
  buildChatConversationByIdQueryKey,
  buildChatConversationsQueryKey,
  buildChatMessagesQueryKey,
  buildChatUnreadCountQueryKey,
  CHAT_CONVERSATIONS_QUERY_KEY,
  CHAT_UNREAD_COUNT_QUERY_KEY,
  OWN_PORTAL_SUMMARY_QUERY_KEY,
} from '@/constants/queryKeys';
import {
  CHAT_ACTIVE_CONVERSATION_REFETCH_INTERVAL_MILLISECONDS,
  CHAT_LIST_REFETCH_INTERVAL_MILLISECONDS,
  CHAT_MESSAGE_PAGE_SIZE,
} from '@/constants/chat';
import { useAuth } from '@/hooks/useAuth';

export interface ChatMessageWithSendStatus extends ChatMessage {
  sendStatus: 'sent' | 'sending' | 'failed';
}

export interface SendChatMessageInput {
  tempId: string;
  body: string | null;
  attachmentIds: string[];
}

function toSentMessage(message: ChatMessage): ChatMessageWithSendStatus {
  return { ...message, sendStatus: 'sent' };
}

export function usePractitionerChatConversationsQuery(
  page: number,
  search: string
): UseQueryResult<PaginatedResult<ChatConversation>, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: buildChatConversationsQueryKey('practitioner', page, search),
    queryFn: function loadConversations() {
      return fetchPractitionerConversations(page, search);
    },
    enabled: status === 'authenticated',
    placeholderData: keepPreviousData,
    refetchInterval: CHAT_LIST_REFETCH_INTERVAL_MILLISECONDS,
  });
}

export function usePractitionerChatConversationByIdQuery(
  conversationId: string | null
): UseQueryResult<ChatConversation, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: buildChatConversationByIdQueryKey(conversationId === null ? '' : conversationId),
    queryFn: function loadConversation() {
      if (conversationId === null) {
        return Promise.reject(new Error('A conversation id is required.'));
      }
      return fetchPractitionerConversationById(conversationId);
    },
    enabled: conversationId !== null && status === 'authenticated',
  });
}

export function useClientChatConversationsQuery(): UseQueryResult<ChatConversation[], Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: buildChatConversationsQueryKey('client', 1, ''),
    queryFn: fetchClientConversations,
    enabled: status === 'authenticated',
    refetchInterval: CHAT_LIST_REFETCH_INTERVAL_MILLISECONDS,
  });
}

export function useChatUnreadCountQuery(scope: ChatScope): UseQueryResult<number, Error> {
  const { status } = useAuth();
  return useQuery({
    queryKey: buildChatUnreadCountQueryKey(scope),
    queryFn: function loadUnreadCount() {
      return fetchChatUnreadCount(scope);
    },
    enabled: status === 'authenticated',
    refetchInterval: CHAT_LIST_REFETCH_INTERVAL_MILLISECONDS,
  });
}

export function useChatMessagesQuery(
  scope: ChatScope,
  conversationId: string | null
): UseQueryResult<ChatMessageWithSendStatus[], Error> {
  const queryClient = useQueryClient();
  const { status } = useAuth();
  return useQuery({
    queryKey: buildChatMessagesQueryKey(scope, conversationId === null ? '' : conversationId),
    queryFn: async function loadMessages() {
      if (conversationId === null) {
        return [];
      }
      const messagesQueryKeyForConversation = buildChatMessagesQueryKey(scope, conversationId);
      const cachedMessagesBeforeFetch = queryClient.getQueryData<ChatMessageWithSendStatus[]>(
        messagesQueryKeyForConversation
      );
      const confirmedMessagesBeforeFetch =
        cachedMessagesBeforeFetch === undefined
          ? []
          : cachedMessagesBeforeFetch.filter(function isConfirmed(message) {
              return message.sendStatus === 'sent';
            });
      const lastConfirmedMessage =
        confirmedMessagesBeforeFetch[confirmedMessagesBeforeFetch.length - 1];
      const fetchedMessages = await fetchConversationMessages(scope, conversationId, {
        afterId: lastConfirmedMessage === undefined ? undefined : lastConfirmedMessage.id,
      });
      const cachedMessagesAfterFetch = queryClient.getQueryData<ChatMessageWithSendStatus[]>(
        messagesQueryKeyForConversation
      );
      const confirmedMessagesAfterFetch =
        cachedMessagesAfterFetch === undefined
          ? []
          : cachedMessagesAfterFetch.filter(function isConfirmed(message) {
              return message.sendStatus === 'sent';
            });
      if (confirmedMessagesAfterFetch.length === 0) {
        return fetchedMessages.map(toSentMessage);
      }
      const knownMessageIds = new Set(
        confirmedMessagesAfterFetch.map(function readId(message) {
          return message.id;
        })
      );
      const newlyFetchedMessages = fetchedMessages
        .filter(function isUnknown(message) {
          return knownMessageIds.has(message.id) === false;
        })
        .map(toSentMessage);
      const pendingMessagesAfterFetch =
        cachedMessagesAfterFetch === undefined
          ? []
          : cachedMessagesAfterFetch.filter(function isPending(message) {
              return message.sendStatus !== 'sent';
            });
      return [
        ...confirmedMessagesAfterFetch,
        ...newlyFetchedMessages,
        ...pendingMessagesAfterFetch,
      ];
    },
    enabled: conversationId !== null && status === 'authenticated',
    refetchInterval: CHAT_ACTIVE_CONVERSATION_REFETCH_INTERVAL_MILLISECONDS,
    refetchIntervalInBackground: false,
  });
}

export interface LoadEarlierChatMessagesResult {
  hasMoreEarlierMessages: boolean;
}

export function useLoadEarlierChatMessagesMutation(
  scope: ChatScope,
  conversationId: string | null
): UseMutationResult<LoadEarlierChatMessagesResult, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function loadEarlierMessages(oldestLoadedMessageId: string) {
      if (conversationId === null) {
        return { hasMoreEarlierMessages: false };
      }
      const messagesQueryKey = buildChatMessagesQueryKey(scope, conversationId);
      await queryClient.cancelQueries({ queryKey: messagesQueryKey });
      const fetchedMessages = await fetchConversationMessages(scope, conversationId, {
        beforeId: oldestLoadedMessageId,
      });
      const earlierMessages = fetchedMessages.map(toSentMessage);
      queryClient.setQueryData<ChatMessageWithSendStatus[]>(
        messagesQueryKey,
        function prependEarlierMessages(previousMessages) {
          const currentMessages = previousMessages === undefined ? [] : previousMessages;
          return [...earlierMessages, ...currentMessages];
        }
      );
      return { hasMoreEarlierMessages: fetchedMessages.length === CHAT_MESSAGE_PAGE_SIZE };
    },
  });
}

export function useSendChatMessageMutation(
  scope: ChatScope,
  conversationId: string | null,
  ownSenderKind: string
): UseMutationResult<ChatMessage, Error, SendChatMessageInput> {
  const queryClient = useQueryClient();
  const messagesQueryKey = buildChatMessagesQueryKey(
    scope,
    conversationId === null ? '' : conversationId
  );

  return useMutation({
    mutationFn: async function send(input: SendChatMessageInput) {
      if (conversationId === null) {
        throw new Error('A conversation must be selected before sending a message.');
      }
      return sendChatMessage(scope, conversationId, input.body, input.attachmentIds);
    },
    onMutate: function applyOptimisticMessage(input: SendChatMessageInput) {
      const optimisticMessage: ChatMessageWithSendStatus = {
        id: input.tempId,
        conversationId: conversationId === null ? '' : conversationId,
        senderKind: ownSenderKind,
        body: input.body,
        attachments: [],
        createdAt: new Date().toISOString(),
        sendStatus: 'sending',
      };
      queryClient.setQueryData<ChatMessageWithSendStatus[]>(
        messagesQueryKey,
        function upsertOptimistic(previousMessages) {
          const withoutSameTempId = (previousMessages === undefined ? [] : previousMessages).filter(
            function isDifferent(message) {
              return message.id !== input.tempId;
            }
          );
          return [...withoutSameTempId, optimisticMessage];
        }
      );
    },
    onError: function markFailed(_sendError, input: SendChatMessageInput) {
      queryClient.setQueryData<ChatMessageWithSendStatus[]>(
        messagesQueryKey,
        function markMessageFailed(previousMessages) {
          if (previousMessages === undefined) {
            return previousMessages;
          }
          return previousMessages.map(function updateStatus(message) {
            return message.id === input.tempId ? { ...message, sendStatus: 'failed' } : message;
          });
        }
      );
    },
    onSuccess: function reconcileWithServerMessage(
      serverMessage: ChatMessage,
      input: SendChatMessageInput
    ) {
      queryClient.setQueryData<ChatMessageWithSendStatus[]>(
        messagesQueryKey,
        function replaceOptimistic(previousMessages) {
          const withoutOptimisticAndDuplicate = (
            previousMessages === undefined ? [] : previousMessages
          ).filter(function isKept(message) {
            return message.id !== input.tempId && message.id !== serverMessage.id;
          });
          return [...withoutOptimisticAndDuplicate, toSentMessage(serverMessage)];
        }
      );
      queryClient.invalidateQueries({ queryKey: CHAT_CONVERSATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CHAT_UNREAD_COUNT_QUERY_KEY });
    },
  });
}

export function useMarkConversationReadMutation(
  scope: ChatScope,
  conversationId: string | null
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async function markRead() {
      if (conversationId === null) {
        return;
      }
      await markConversationRead(scope, conversationId);
    },
    onSuccess: function refresh() {
      queryClient.invalidateQueries({ queryKey: CHAT_CONVERSATIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CHAT_UNREAD_COUNT_QUERY_KEY });
      if (scope === 'client') {
        queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
      }
    },
  });
}

export function useInitChatAttachmentUploadMutation(
  scope: ChatScope,
  conversationId: string | null
): UseMutationResult<ChatAttachmentInit, Error, ChatAttachmentInitPayload> {
  return useMutation({
    mutationFn: async function initUpload(payload: ChatAttachmentInitPayload) {
      if (conversationId === null) {
        throw new Error('A conversation must be selected before attaching a file.');
      }
      return initChatAttachmentUpload(scope, conversationId, payload);
    },
  });
}

export function useCreatePractitionerConversationMutation(): UseMutationResult<
  ChatConversation,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPractitionerConversation,
    onSuccess: function refresh() {
      queryClient.invalidateQueries({ queryKey: CHAT_CONVERSATIONS_QUERY_KEY });
    },
  });
}
